import React, { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import StageColumn from './StageColumn';
import OpportunityCard from './OpportunityCard';

const KanbanBoard = ({
  stages = [],
  opportunities = [],
  colorMode = 'colored_dot',
  onMoveOpportunity,
  loading = false,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [collapsedStages, setCollapsedStages] = useState(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group opportunities by stage
  const opportunitiesByStage = useMemo(() => {
    const grouped = {};
    stages.forEach((stage) => {
      grouped[stage.id] = [];
    });
    opportunities.forEach((opp) => {
      if (grouped[opp.stage_id]) {
        grouped[opp.stage_id].push(opp);
      }
    });
    return grouped;
  }, [stages, opportunities]);

  // Find the active opportunity being dragged
  const activeOpportunity = useMemo(() => {
    if (!activeId) return null;
    return opportunities.find((o) => o.id === activeId);
  }, [activeId, opportunities]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeOpp = opportunities.find((o) => o.id === active.id);
    if (!activeOpp) return;

    // Determine the target stage
    let targetStageId = null;

    // Check if dropped on a stage directly
    if (over.data?.current?.type === 'stage') {
      targetStageId = over.id;
    } 
    // Check if dropped on another opportunity
    else if (over.data?.current?.type === 'opportunity') {
      const targetOpp = opportunities.find((o) => o.id === over.id);
      if (targetOpp) {
        targetStageId = targetOpp.stage_id;
      }
    }
    // Fallback: check if over.id matches a stage
    else if (stages.find((s) => s.id === over.id)) {
      targetStageId = over.id;
    }

    if (!targetStageId || targetStageId === activeOpp.stage_id) return;

    // Call the move handler
    onMoveOpportunity?.(activeOpp.id, activeOpp.stage_id, targetStageId);
  }, [opportunities, stages, onMoveOpportunity]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const toggleCollapse = useCallback((stageId) => {
    setCollapsedStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <div className="text-center">
          <p className="font-medium">No pipeline selected</p>
          <p className="text-sm mt-1">Select a pipeline to view the board</p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div 
        className="flex gap-3 overflow-x-auto pb-4 px-1"
        style={{ minHeight: 'calc(100vh - 280px)' }}
        data-testid="kanban-board"
      >
        {stages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            opportunities={opportunitiesByStage[stage.id] || []}
            colorMode={colorMode}
            isCollapsed={collapsedStages.has(stage.id)}
            onToggleCollapse={toggleCollapse}
          />
        ))}
      </div>

      {/* Drag overlay - shows the card being dragged */}
      <DragOverlay>
        {activeOpportunity ? (
          <OpportunityCard opportunity={activeOpportunity} isDragging overlay />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
